<script setup>
import ModalMatchDetail from "@/components/modal/match-detail-modal.vue";
import { defineProps, ref, computed, watch, onMounted } from "vue";
import { useQuery, useLazyQuery } from "@vue/apollo-composable";
import {
  GET_TOUR_MATCHES,
  GET_CATEGORIES,
} from "@/graphql/operations/tour-match.ts";
import { formatNewDate, getTime } from "@/utils/datetime";
import { useRoute } from "vue-router";

const route = useRoute();

const { tournament } = defineProps({
  tournament: { type: Object, default: null },
});

const keywordInput = ref("");
const keyword = ref("");

const selectedStatus = ref("");
const limitData = ref("limit");
const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(0);
const categoryFilter = ref("all");
const categorieOptions = ref([]);

const initialData = ref(null);
const showModal = ref(false);

const filters = computed(() => {
  const baseFilters = {
    tournament: {
      documentId: { eq: tournament?.documentId },
    },
    and: [],
    or: [],
  };

  if (selectedStatus.value) {
    baseFilters.tour_match_status = {
      eq: selectedStatus.value,
    };
  }

  if (limitData.value && limitData.value === "limit") {
    baseFilters.and.push({
      or: [
        { tour_player_team_1: { player_1: { documentId: { ne: null } } } },
        { tour_player_team_1: { player_2: { documentId: { ne: null } } } },
        { tour_player_team_2: { player_1: { documentId: { ne: null } } } },
        { tour_player_team_2: { player_2: { documentId: { ne: null } } } },
      ],
    });
  }

  if (categoryFilter.value !== "all") {
    baseFilters.category =
      categoryFilter.value === "team"
        ? {
            documentId: {
              eq: null,
            },
          }
        : {
            documentId: {
              eq: categoryFilter.value,
            },
          };
  }

  if (keyword.value) {
    baseFilters.or.push(
      {
        tour_player_team_1: {
          or: [
            {
              player_1: {
                display_name: {
                  contains: keyword.value,
                },
              },
            },
            {
              player_2: {
                display_name: {
                  contains: keyword.value,
                },
              },
            },
          ],
        },
      },
      {
        tour_player_team_2: {
          or: [
            {
              player_1: {
                display_name: {
                  contains: keyword.value,
                },
              },
            },
            {
              player_2: {
                display_name: {
                  contains: keyword.value,
                },
              },
            },
          ],
        },
      },
      {
        category: {
          name: {
            contains: keyword.value,
          },
        },
      },
      {
        match_code: {
          contains: keyword.value,
        },
      },
    );
  }

  return {
    filters: baseFilters,
    pagination: {
      page: parseInt(currentPage.value),
      pageSize: parseInt(pageSize.value),
    },
    sort: ["start_date:desc"],
  };
});

const { result, refetch } = useQuery(
  GET_TOUR_MATCHES,
  () => ({
    sort: filters.value.sort,
    pagination: filters.value.pagination,
    filters: filters.value.filters,
  }),
  { fetchPolicy: "no-cache" },
);

watch(result, (data) => {
  const pageInfo = data?.tourMatches_connection?.meta?.pagination;
  totalPages.value = pageInfo?.pageCount || 0;
});

const matches = computed(
  () => result.value?.tourMatches_connection?.nodes || [],
);

const searchKeyword = () => {
  keyword.value = keywordInput.value;
  currentPage.value = 1;
  refetch();
};

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const getPaginationRange = () => {
  const maxVisiblePages = 5; // Số lượng trang hiển thị tối đa
  const halfVisible = Math.floor(maxVisiblePages / 2);

  let startPage = Math.max(1, currentPage.value - halfVisible);
  let endPage = Math.min(totalPages.value, startPage + maxVisiblePages - 1);

  // Điều chỉnh lại startPage nếu endPage đã đạt giới hạn
  if (endPage === totalPages.value) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return pages;
};

const handleTableChange = () => {
  currentPage.value = 1;
};

const getTypeCategoryText = (type) => {
  switch (type) {
    case "SM":
      return "Đơn nam";
    case "DW":
      return "Đôi nữ";
    case "DM":
      return "Đôi nam";
    case "SW":
      return "Đơn nữ";
    default:
      return "Đôi nam nữ";
  }
};

const getStatusClass = (status) => {
  switch (status) {
    case "NOT_SCHEDULED":
      return "badge bg-secondary";
    case "SCHEDULED":
      return "badge bg-info";
    case "ONGOING":
      return "badge bg-primary";
    case "PAUSED":
      return "badge bg-warning";
    case "COMPLETED":
      return "badge bg-success";
    default:
      return "badge bg-danger";
  }
};

const getStatusText = (status) => {
  switch (status) {
    case "NOT_SCHEDULED":
      return "Chưa lên lịch";
    case "SCHEDULED":
      return "Đã lên lịch";
    case "ONGOING":
      return "Đang diễn ra";
    case "PAUSED":
      return "Tạm hoãn";
    case "COMPLETED":
      return "Đã kết thúc";
    default:
      return "Đã huỷ";
  }
};

const statusOptions = [
  { label: "Tất cả", value: "" },
  { label: "Đã lên lịch", value: "SCHEDULED" },
  { label: "Đang diễn ra", value: "ONGOING" },
  { label: "Đã kết thúc", value: "COMPLETED" },
];

const convertScore = (score) => {
  const arr = JSON.parse(score);
  if (!Array.isArray(arr) || arr.length !== 2) return null;
  return arr.join(" - ");
};

const getRoundType = (type) => {
  if (type === "knockout") {
    return "Loại trực tiếp";
  } else {
    return "Vòng tròn";
  }
};

watch(
  () => tournament,
  (newVal) => {
    if (newVal) {
      refetch();
      loadCategories();
    } else {
      currentPage.value = 1;
    }
  },
);

const openModal = (record) => {
  showModal.value = true;
  initialData.value = record;
};

const searchStatus = () => {
  refetch({
    sort: filters.value.sort,
    pagination: filters.value.pagination,
    filters: filters.value.filters,
  });
};

const searchData = () => {
  refetch({
    sort: filters.value.sort,
    pagination: filters.value.pagination,
    filters: filters.value.filters,
  });
};

const showTime = (set_times) => {
  try {
    const arr = JSON.parse(set_times);

    if (!Array.isArray(arr) || arr.length === 0) return "";

    const dates = [];
    const times = [];

    arr.flat().forEach((item) => {
      const [date, time] = item.split(" ");
      dates.push(date);
      times.push(time);
    });

    const startDate = dates[0] ?? "---";
    const endDate = dates[dates.length - 1] ?? "---";
    const startTime = times[0] ?? "---";
    const endTime = times[times.length - 1] ?? "---";

    if (startDate === endDate) {
      return `<p class="text-center">${startDate}<br>${startTime} - ${endTime}</p>`;
    } else {
      return `<p class="text-center">${startDate} - ${endDate}<br>${startTime} - ${endTime}</p>`;
    }
  } catch (e) {
    console.error("Error parsing set_times:", e);
    return "";
  }
};

const showScore = (score_detail) => {
  const arr = JSON.parse(score_detail);
  if (!Array.isArray(arr) || arr.length === 0) return "";
  return arr
    .map((pair, index) => `<span>G${index + 1}: ${pair[0]}-${pair[1]}</span>`)
    .join("\n");
};

const { onResult: onCategoriesResult, load: loadCategories } = useLazyQuery(
  GET_CATEGORIES,
  () => ({
    documentId: tournament?.documentId,
    page: 1,
    pageSize: 100,
  }),
);

onCategoriesResult(({ data }) => {
  const { categories_connection } = data;
  categorieOptions.value = categories_connection.nodes.map((category) => ({
    value: category.documentId,
    label: category.name,
  }));

  if (tournament.type !== "single") {
    categorieOptions.value.unshift({
      value: "team",
      label: "Đồng đội",
    });
  }
  categorieOptions.value.unshift({
    value: "all",
    label: "Tất cả",
  });
});

onMounted(() => {
  console.log(route.query.match_code);
  if (route.query.match_code) {
    keywordInput.value = route.query.match_code;
    searchKeyword();
  }
});
</script>

<template>
  <div class="coache-head-blk mb-3">
    <div class="d-flex justify-content-end align-items-center">
      <div class="w-100">
        <div class="d-flex w-100 justify-content-between align-items-end">
          <div id="tablefilter">
            <div class="dataTables_filter d-flex gap-2 flex-wrap">
              <div
                class="w-25 d-flex flex-column justify-content-start align-items-start"
                style="min-width: 200px"
              >
                <label
                  class="d-flex justify-content-between align-items-center gap-2 text-nowrap"
                >
                  Trạng thái:
                </label>
                <select
                  v-model="selectedStatus"
                  class="form-control"
                  style="min-width: 200px"
                  @change="searchStatus"
                >
                  <option
                    v-for="item in statusOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </div>

              <div
                class="w-25 d-flex flex-column justify-content-start align-items-start"
                style="min-width: 200px"
              >
                <label
                  class="d-flex justify-content-between align-items-center gap-2 text-nowrap"
                >
                  Nội dung:
                </label>
                <select
                  v-model="categoryFilter"
                  class="form-control"
                  style="min-width: 200px"
                  @change="searchData"
                >
                  <option
                    v-for="item in categorieOptions"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </div>

              <div
                class="w-25 d-flex flex-column justify-content-start align-items-start"
                style="min-width: 200px"
              >
                <label
                  class="d-flex justify-content-between align-items-center gap-2 text-nowrap"
                >
                  Từ khóa:
                </label>
                <input
                  v-model="keywordInput"
                  class="form-control form-control-sm"
                  placeholder="Tìm kiếm tên, nội dung,..."
                  style="min-width: unset"
                  type="search"
                />
              </div>
            </div>
          </div>
          <div class="request-coach-list">
            <div class="card-header-btns">
              <nav>
                <div class="nav nav-tabs" role="tablist">
                  <button
                    class="nav-link active text-nowrap"
                    type="button"
                    @click="searchKeyword"
                  >
                    Tìm kiếm
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="table-responsive table-datatble">
    <table class="table table-borderless dashboard-card-table">
      <colgroup>
        <col width="100px" />
        <col width="100px" />
        <col width="100px" />
        <col width="300px" />
        <col width="x" />
        <col width="300px" />
        <col width="100px" />
        <col width="100px" />
        <col width="100px" />
        <col width="100px" />
      </colgroup>
      <thead class="thead-light">
        <tr>
          <th>Mã trận</th>
          <th>Nội dung</th>
          <th>Đội 1</th>
          <th>Tỉ số</th>
          <th>Đội 2</th>
          <th>Hành động</th>
          <th>Trạng thái</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in matches" :key="item.id">
          <td>
            <a href="javascript:void(0);" @click="openModal(item)">
              {{ item?.match_code }}
            </a>
          </td>
          <td>
            <template v-if="item.tour_category">
              {{
                item.tour_category
                  ? `Đồng đội - ${getTypeCategoryText(item.tour_category)}`
                  : item.tour_category
              }}
            </template>
            <template v-else>
              {{ item.category?.name || "" }}
            </template>
          </td>
          <th scope="row">
            {{ item?.tour_player_team_1?.player_1?.display_name }}
            <br />
            {{ item?.tour_player_team_1?.player_2?.display_name }}
          </th>
          <td>
            <a :href="item?.replay_url ?? '#'" target="_blank">
              <h4>{{ convertScore(item?.score) }}</h4>
            </a>
            <p
              class="mb-0 mt-1 small d-flex justify-content-start flex-column"
              v-html="showScore(item?.score_detail)"
            ></p>
          </td>
          <th scope="row">
            {{ item?.tour_player_team_2?.player_1?.display_name }}
            <br />
            {{ item?.tour_player_team_2?.player_2?.display_name }}
          </th>

          <td>
            <a
              v-if="
                item?.tour_match_status === 'COMPLETED' &&
                item?.replay_url &&
                !['#', ''].includes(item.replay_url)
              "
              :href="item.replay_url"
              class="badge bg-success bg-opacity-25 text-white"
              target="_blank"
            >
              Xem lại
            </a>
          </td>
          <td>
            <p
              :class="getStatusClass(item?.tour_match_status)"
              class="text-white"
            >
              {{ getStatusText(item?.tour_match_status) }}
            </p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <div class="tab-footer">
    <div class="row">
      <div class="col-md-6">
        <div id="tablelength">
          <div class="dataTables_length">
            <label
              >Hiển thị
              <select
                v-model="pageSize"
                class="custom-select custom-select-sm form-control form-control-sm"
                @change="handleTableChange"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="999999">All</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div id="tablepage">
          <div class="dataTables_paginate paging_simple_numbers">
            <ul class="pagination">
              <li
                :class="{ disabled: currentPage === 1 }"
                class="paginate_button page-item previous"
                @click="changePage(currentPage - 1)"
              >
                <a class="page-link" href="javascript:void(0);">
                  <i class="feather-chevrons-left"></i>
                </a>
              </li>

              <!-- Nút trang đầu tiên -->
              <li
                v-if="getPaginationRange()[0] > 1"
                class="paginate_button page-item"
                @click="changePage(1)"
              >
                <a class="page-link" href="javascript:void(0);">1</a>
              </li>

              <!-- Dấu ellipsis đầu -->
              <li
                v-if="getPaginationRange()[0] > 2"
                class="paginate_button page-item disabled"
              >
                <a class="page-link" href="javascript:void(0);">...</a>
              </li>

              <!-- Các trang trong khoảng hiển thị -->
              <li
                v-for="page in getPaginationRange()"
                :key="page"
                :class="{ active: currentPage === page }"
                class="paginate_button page-item"
                @click="changePage(page)"
              >
                <a class="page-link" href="javascript:void(0);">{{ page }}</a>
              </li>

              <!-- Dấu ellipsis cuối -->
              <li
                v-if="
                  getPaginationRange()[getPaginationRange().length - 1] <
                  totalPages - 1
                "
                class="paginate_button page-item disabled"
              >
                <a class="page-link" href="javascript:void(0);">...</a>
              </li>

              <!-- Nút trang cuối cùng -->
              <li
                v-if="
                  getPaginationRange()[getPaginationRange().length - 1] <
                  totalPages
                "
                class="paginate_button page-item"
                @click="changePage(totalPages)"
              >
                <a class="page-link" href="javascript:void(0);">{{
                  totalPages
                }}</a>
              </li>

              <li
                :class="{ disabled: currentPage === totalPages }"
                class="paginate_button page-item next"
                @click="changePage(currentPage + 1)"
              >
                <a class="page-link" href="javascript:void(0);">
                  <i class="feather-chevrons-right"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ModalMatchDetail
    v-model:visible="showModal"
    :initial-data="initialData"
    :style="{ width: '50%', minWidth: '600px' }"
  />
</template>

<style scoped>
.badge.bg-success {
  background-color: rgba(27, 179, 51, 1) !important;
  color: #097e52;
}

.badge.bg-info {
  background-color: rgba(119, 86, 201, 1) !important;
  color: #7756c9;
}

.badge.bg-danger {
  background-color: rgba(245, 3, 3, 1) !important;
  color: #f50303;
}

.badge.bg-warning {
  background-color: rgba(12, 174, 244, 1) !important;
  color: #0caef4;
}
</style>
